import { createSelector, createStructuredSelector } from 'reselect';
import {
    selectAvailabilityResult,
    selectCheckedAddress,
    selectTvConnect,
    toAddressString,
    JarvisTvProduct,
    AvailabilityCheckResponse,
    JarvisPublicFact,
    selectPublicFacts,
    JarvisProduct,
    selectTvConnectBroadvision,
    selectGigaTv,
} from '@vfde-sails/availability';
import { ProductType } from '../../enum';
import { ProductAvailability } from './interface';
import { selectProductConfig, selectSelectedId } from '../App/selector';
import { ProductConfig } from '../App/interface';
import { selectCustomer } from '@vfde-sails/login';

const isProductTypeAvailable = (config: ProductConfig, products: JarvisProduct[], type: ProductType) =>
    Boolean(products.find(product => config[product.id]?.type === type));

const getAvailabilityMap = (
    isCableAvailable: boolean,
    isNetAvailable: boolean,
    result?: AvailabilityCheckResponse,
    tvConnect?: JarvisTvProduct,
): ProductAvailability => {

    return {
        cableTv: isCableAvailable,
        netTv: isNetAvailable,
        tvConnect: Boolean(tvConnect),
        homesPassed: Boolean(result?.marketability?.isCableConnection),
    };
};

const isBookingDisabled = (facts?: JarvisPublicFact[]) =>
    Boolean([
        JarvisPublicFact.ThreePlayProductCustomer,
        JarvisPublicFact.DunningStatusCustomer,
        JarvisPublicFact.VkdKudUmeCustomer,
        JarvisPublicFact.OpenOrdersCustomer,
    ].find(fact => facts?.includes(fact)));

const isNonMonthlyBilling = (facts?: JarvisPublicFact[]) =>
    Boolean(facts?.includes(JarvisPublicFact.NonMonhtlyBillingTv));

const isDigitalTvCusomer = (facts: JarvisPublicFact[] = [], isCableAvailable: boolean, isNetAvailable: boolean) =>
    Boolean(facts.includes(JarvisPublicFact.DigitalTvProductCustomer) || (!isCableAvailable && !isNetAvailable));

export const selectAddress = createSelector(selectCheckedAddress, toAddressString);
export const selectIsCableTvAvailable = createSelector(
    selectAvailabilityResult,
    selectProductConfig,
    selectGigaTv,
    (result, c, p) => !result || isProductTypeAvailable(c, p, ProductType.CABLE)
);
export const selectIsNetTvAvailable = createSelector(
    selectAvailabilityResult,
    selectProductConfig,
    selectGigaTv,
    (result, c, p) => !result || isProductTypeAvailable(c, p, ProductType.NET)
);
export const selectAvailability = createSelector(
    selectIsCableTvAvailable,
    selectIsNetTvAvailable,
    selectAvailabilityResult,
    selectTvConnect,
    getAvailabilityMap
);
export const selectIsNotMonthlyBilling = createSelector(selectPublicFacts, isNonMonthlyBilling);
export const selectIsBookingDisabled = createSelector(selectPublicFacts, isBookingDisabled);
export const selectIsTvCustomer = createSelector(selectPublicFacts, selectIsCableTvAvailable, selectIsNetTvAvailable, isDigitalTvCusomer);

/**
 * Create a structured selector. (Map our selectors into specific keys in an object)
 * Used to connect the state to the container
 */
export const mapStateToProps = createStructuredSelector({
    selectedId: selectSelectedId,
    address: selectAddress,
    availability: selectAvailability,
    result: selectAvailabilityResult,
    tvConnect: selectTvConnect,
    tvConnectBv: selectTvConnectBroadvision,
    customer: selectCustomer,
});
